import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { fetchMe } from '../api/me'
import { listPartnerThreads } from '../api/threads'
import type { AuthSession, MeResponse, OrganizationDto, UserRole } from '../api/types'
import type { MessengerBridge } from '../bridge'
import { isDevMode } from '../config/devMode'
import { isRestaurantOnly } from '../config/restaurantOnly'
import { useCart } from '../hooks/useCart'
import { entitlementsFromOrg } from '../hooks/useEntitlements'
import { useUserContext } from '../hooks/useUserContext'
import { safeDisplayText } from '../utils/pdnDisplay'
import { DEFAULT_TAB, TabId } from '../navigation/tabs'
import { AnalyticsScreen } from '../screens/AnalyticsScreen'
import { CartScreen } from '../screens/CartScreen'
import { ChatScreen, type ChatPartnerSelection } from '../screens/ChatScreen'
import { ImportWizardScreen } from '../screens/ImportWizardScreen'
import { OrdersScreen } from '../screens/OrdersScreen'
import { PricesScreen, type ImportStartMode } from '../screens/PricesScreen'
import { ProfileScreen } from '../screens/ProfileScreen'
import { SearchScreen } from '../screens/SearchScreen'
import { BottomTabBar } from './BottomTabBar'
import { DevModeBanner } from './DevModeBanner'

type AppLayoutProps = {
  bridge: MessengerBridge
  session: AuthSession
  devRole?: UserRole
  onDevSessionChange?: (session: AuthSession, role: UserRole) => void
}

const tabTitle = (tabId: TabId): string => {
  switch (tabId) {
    case TabId.Orders:
      return 'Заявки'
    case TabId.Chat:
      return 'Чаты'
    case TabId.Search:
      return 'Поиск'
    case TabId.Cart:
      return 'Корзина'
    case TabId.Prices:
      return 'Прайсы'
    case TabId.Analytics:
      return 'Аналитика'
    default:
      return 'Провизиум'
  }
}

export const AppLayout = ({
  bridge,
  session,
  devRole,
  onDevSessionChange,
}: AppLayoutProps) => {
  const userContext = useUserContext(
    session,
    isRestaurantOnly ? 'buyer' : devRole,
  )
  const { count: cartCount } = useCart()
  const [activeTab, setActiveTab] = useState<TabId>(DEFAULT_TAB)
  const [chatPartner, setChatPartner] = useState<ChatPartnerSelection | null>(null)
  const [chatUnreadCount, setChatUnreadCount] = useState(0)
  const [showProfile, setShowProfile] = useState(false)
  const [importMode, setImportMode] = useState<ImportStartMode | null>(null)
  const [me, setMe] = useState<MeResponse | null>(null)
  const [activeOrganizationId, setActiveOrganizationId] = useState(session.organizationId)

  useEffect(() => {
    setActiveOrganizationId(session.organizationId)
  }, [session.organizationId])

  useEffect(() => {
    let cancelled = false
    void fetchMe()
      .then((profile) => {
        if (!cancelled) {
          setMe(profile)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMe(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [session.accessToken])

  const organizations: OrganizationDto[] = me?.organizations ?? []
  const activeOrg =
    organizations.find((org) => org.id === activeOrganizationId) ?? null
  const entitlements = entitlementsFromOrg(activeOrg)
  const organizationId = activeOrganizationId

  const refreshUnread = useCallback(async () => {
    if (isRestaurantOnly) {
      return
    }
    try {
      const threads = await listPartnerThreads(session.organizationId)
      setChatUnreadCount(threads.reduce((total, thread) => total + thread.unreadCount, 0))
    } catch {
      // Бейдж непрочитанных — best effort
    }
  }, [session.organizationId])

  useEffect(() => {
    if (isRestaurantOnly) {
      return
    }
    void refreshUnread()
    const timer = window.setInterval(() => {
      void refreshUnread()
    }, 30_000)
    return () => window.clearInterval(timer)
  }, [refreshUnread])

  useEffect(() => {
    if (chatPartner == null) {
      void refreshUnread()
    }
  }, [chatPartner, refreshUnread])

  const platformLabel = bridge.platform === 'telegram' ? 'Telegram' : 'MAX'

  const handleOpenChat = (counterpartyOrganizationId: number, counterpartyName: string) => {
    if (isRestaurantOnly) {
      return
    }
    setChatPartner({
      organizationId: counterpartyOrganizationId,
      name: counterpartyName,
    })
    setActiveTab(TabId.Chat)
  }

  const content: ReactNode = (() => {
    if (isRestaurantOnly) {
      switch (activeTab) {
        case TabId.Cart:
          return <CartScreen organizationId={organizationId} bridge={bridge} />
        case TabId.Prices:
          return (
            <PricesScreen
              organizationId={organizationId}
              onStartImport={setImportMode}
            />
          )
        case TabId.Analytics:
          return (
            <AnalyticsScreen
              organizationId={organizationId}
              hasPremium={entitlements.marketPriceIntelligenceEnabled}
              onOpenProfile={() => setShowProfile(true)}
            />
          )
        case TabId.Search:
        default:
          return <SearchScreen organizationId={organizationId} />
      }
    }

    switch (activeTab) {
      case TabId.Chat:
        return (
          <ChatScreen
            organizationId={session.organizationId}
            role={userContext.role}
            active={activeTab === TabId.Chat}
            partner={chatPartner}
            onSelectPartner={setChatPartner}
          />
        )
      case TabId.Orders:
      default:
        return (
          <OrdersScreen
            bridge={bridge}
            session={session}
            userContext={userContext}
            initialRfqId={session.rfqId}
            onOpenChat={handleOpenChat}
          />
        )
    }
  })()

  const inChatThread = !isRestaurantOnly && activeTab === TabId.Chat && chatPartner != null

  useEffect(() => {
    if (!inChatThread) {
      return
    }
    bridge.setBackHandler(() => setChatPartner(null))
    return () => bridge.setBackHandler(null)
  }, [bridge, inChatThread])

  useEffect(() => {
    if (showProfile || importMode) {
      bridge.setBackHandler(() => {
        if (importMode) {
          setImportMode(null)
          return
        }
        setShowProfile(false)
      })
      return () => bridge.setBackHandler(null)
    }
  }, [bridge, importMode, showProfile])

  useEffect(() => {
    const root = document.documentElement
    const shell = document.querySelector('.app-shell')

    if (inChatThread) {
      root.classList.add('app-chat-thread-lock')
      shell?.classList.add('app-shell--chat-thread')
      const tg = window.Telegram?.WebApp as { viewportStableHeight?: number } | undefined
      if (tg?.viewportStableHeight) {
        root.style.setProperty('--app-vh', `${tg.viewportStableHeight}px`)
      }
    } else {
      root.classList.remove('app-chat-thread-lock')
      shell?.classList.remove('app-shell--chat-thread')
      root.style.removeProperty('--app-vh')
    }

    return () => {
      root.classList.remove('app-chat-thread-lock')
      shell?.classList.remove('app-shell--chat-thread')
      root.style.removeProperty('--app-vh')
    }
  }, [inChatThread])

  if (importMode) {
    return (
      <div className="app-layout">
        <ImportWizardScreen
          organizationId={organizationId}
          mode={importMode}
          onClose={() => setImportMode(null)}
          onDone={() => {
            setImportMode(null)
            setActiveTab(TabId.Prices)
          }}
        />
      </div>
    )
  }

  return (
    <div
      className={`app-layout${inChatThread ? ' app-layout--thread' : ''}${isRestaurantOnly ? ' app-layout--restaurant' : ''}`}
    >
      {isDevMode && onDevSessionChange && !inChatThread && !isRestaurantOnly ? (
        <DevModeBanner
          session={session}
          devRole={devRole ?? userContext.role}
          onSessionChange={onDevSessionChange}
          onRoleSwitch={() => setChatPartner(null)}
        />
      ) : null}
      {!inChatThread ? (
        <header className={isRestaurantOnly ? 'rp-appbar' : 'app-layout__header'}>
          {isRestaurantOnly ? (
            <>
              <div className="rp-appbar__logo" aria-hidden="true">
                П
              </div>
              <div className="rp-appbar__titles">
                <p className="rp-appbar__eyebrow">Провизиум · {platformLabel}</p>
                <h1 className="rp-appbar__title">{tabTitle(activeTab)}</h1>
                {activeOrg?.trade_name || activeOrg?.legal_name || userContext.organizationName ? (
                  <p className="rp-appbar__org">
                    {safeDisplayText(
                      activeOrg?.trade_name ?? activeOrg?.legal_name ?? userContext.organizationName,
                      '',
                    )}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="rp-appbar__gear"
                aria-label="Профиль и настройки"
                onClick={() => setShowProfile(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="8" r="3.2" />
                  <path d="M5 20c0-3.3 3.1-5 7-5s7 1.7 7 5" />
                </svg>
              </button>
            </>
          ) : (
            <div className="app-layout__header-row">
              <div>
                <p className="eyebrow">Провизиум · {platformLabel}</p>
                <h1 className="app-layout__title">{tabTitle(activeTab)}</h1>
                {activeOrg?.trade_name || activeOrg?.legal_name || userContext.organizationName ? (
                  <p className="app-layout__subtitle">
                    {safeDisplayText(
                      activeOrg?.trade_name ?? activeOrg?.legal_name ?? userContext.organizationName,
                      '',
                    )}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </header>
      ) : null}

      <div className="app-layout__content">{content}</div>

      <BottomTabBar
        activeTab={activeTab}
        chatUnreadCount={chatUnreadCount}
        cartCount={cartCount}
        analyticsLocked={!entitlements.marketPriceIntelligenceEnabled}
        onTabChange={(tabId) => {
          if (tabId !== TabId.Chat) {
            setChatPartner(null)
          }
          setActiveTab(tabId)
        }}
      />

      {showProfile ? (
        <div className="rp-sheet" role="dialog" aria-modal="true" aria-labelledby="profile-sheet-title">
          <button
            type="button"
            className="rp-sheet__scrim"
            aria-label="Закрыть профиль"
            onClick={() => setShowProfile(false)}
          />
          <div className="rp-sheet__panel rp-sheet__panel--profile">
            <div className="rp-sheet__grip" aria-hidden="true" />
            <div className="rp-sheet__head">
              <h2 id="profile-sheet-title" className="rp-sheet__title">Профиль и настройки</h2>
            </div>
            <div className="rp-sheet__body">
              <ProfileScreen
                bridge={bridge}
                displayName={safeDisplayText(me?.displayName, 'Пользователь')}
                email={safeDisplayText(me?.email, '')}
                organizations={organizations}
                activeOrganizationId={organizationId}
                onSelectOrganization={setActiveOrganizationId}
                entitlements={entitlements}
                onBack={() => setShowProfile(false)}
                embedded
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
